import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Bell} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useRestaurantContext } from '@/contexts/RestaurantContext'
import { useOrders } from '@/hooks/useOrders'
import { useCurrentUser } from '@/hooks/useAuth'
import { useCurrentAssistant } from '@/hooks/useAssistantAuth'

export function NotificationBell() {
  const { t } = useTranslation()
  const { data: user } = useCurrentUser()
  const { data: assistant } = useCurrentAssistant()
  const { selectedRestaurantId } = useRestaurantContext()
  const { data: allOrders = [] } = useOrders(selectedRestaurantId)
  const [pendingCount, setPendingCount] = useState(0)
  const [hasNewNotification, setHasNewNotification] = useState(false)

  // Check if assistant has cancellations.view permission
  const assistantCanViewCancellations = assistant?.permissions?.cancellations?.view === true

  // Calculate pending cancellation requests
  useEffect(() => {
    if (!allOrders || allOrders.length === 0) {
      setPendingCount(0)
      return
    }

    let count = 0
    allOrders.forEach((order) => {
      // Check parent orders - pending if requested AND status is not cancelled
      if (!order.parentOrderId && order.cancellationRequestedBy && order.status !== 'cancelled') {
        count++
      }

      // Check sub-orders
      if (order.subOrders && order.subOrders.length > 0) {
        order.subOrders.forEach((subOrder) => {
          if (subOrder.cancellationRequestedBy && subOrder.status !== 'cancelled') {
            count++
          }
        })
      }
    })

    setPendingCount(count)
  }, [allOrders])

  // Listen for cancellation request notifications
  useEffect(() => {
    const handleCancellationRequested = (event: CustomEvent) => {
      const { restaurantId: eventRestaurantId } = event.detail || {}
      
      // Only show notification if it's for the current restaurant
      if (eventRestaurantId && eventRestaurantId !== selectedRestaurantId) {
        return
      }
      
      // Play sound notification
      try {
        const audio = new Audio('/notification.mp3')
        audio.play().catch(() => {
          console.log('Audio notification failed')
        })
      } catch (error) {
        console.error('Failed to play notification sound:', error)
      }
      
      // Show visual indicator
      setHasNewNotification(true)
      
      // Reset after 5 seconds
      setTimeout(() => {
        setHasNewNotification(false)
      }, 5000)
    }

    window.addEventListener('cancellation-requested', handleCancellationRequested as EventListener)
    return () => {
      window.removeEventListener('cancellation-requested', handleCancellationRequested as EventListener)
    }
  }, [selectedRestaurantId])

  // Show for admin users OR assistants with cancellations.view permission
  const isAdmin = user?.role === 'admin'
  const canViewCancellations = isAdmin || assistantCanViewCancellations

  if (!canViewCancellations) {
    return null
  }

  return (
    <Link to="/admin-cancellations" className="relative overflow-visible" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="ghost"
        size="icon"
        className="relative overflow-visible"
        title={t('order.pendingCancellationRequests')}
      >
        <Bell className={`h-5 w-5 ${hasNewNotification ? 'text-orange-500 animate-pulse' : ''}`} />
        {pendingCount > 0 && (
          <Badge
            className="absolute -top-0.5 -right-0.5 h-5 w-5 flex items-center justify-center p-0 bg-orange-500 text-white text-xs font-bold rounded-full min-w-[20px] z-10"
          >
            {pendingCount > 9 ? '9+' : pendingCount}
          </Badge>
        )}
      </Button>
    </Link>
  )
}

